import { BuilderState } from '@/types/builder';
import { IconCheck } from '@tabler/icons-react';
import Image from 'next/image';
import React, { useCallback } from 'react';
import Card from '@tempcover/bolt-ui/card';
import { JourneyType, LandingPageForm } from '../LandingPageForm/LandingPageForm';
import TrustPilotWidget, { TrustpilotLayouts } from '../TrustPilot/TrustPilot';
import { CONTENT_TYPES, ContentType } from './PageHero.types';
import { AppStoreLinks } from '@/components/global/Footer/Footer';
import { StoreLinks } from '@/types/storeLinks';

interface ProductPageHeaderProps {
  headingOnly?: boolean;
  enableDiscountBanner?: boolean;
  title: string;
  subtitle?: string;
  tagline?: string;
  badge?: string;
  contentType: ContentType;
  points?: { point: string }[];
  image?: string;
  imageFit?: 'cover' | 'contain';
  imagePosition?: 'left' | 'right';
  body?: string;
  enableForm?: boolean;
  regLabel: string;
  showRegLabel?: boolean;
  showRegToggle?: boolean;
  defaultDurationType?: string;
  daysOnly?: boolean;
  useBanner?: boolean;
  bannerCoverType?: string;
  bannerCoverDuration?: string;
  checkboxLabel?: string;
  formIntroText?: string;
  submitButtonText?: string;
  submitButtonColor?: 'orange' | 'purple';
  builderState: BuilderState;
  journey: JourneyType;
  regPlateOnly: boolean;
  showDurationPicker?: boolean;
  showAppLinks?: boolean;
  appLinks?: StoreLinks;
}

const FormLayout: React.FC<{ props: ProductPageHeaderProps }> = ({ props }) => (
  <div className="grid md:grid-cols-[auto_1fr] md:grid-rows-[1fr_min-content_2fr] md:items-center md:gap-x-20 md:align-middle">
    <Card className="order-2 !h-auto md:order-1 md:row-span-3 md:max-w-[375px]">
      <LandingPageForm
        journey={props.journey}
        regLabel={props.regLabel}
        showRegLabel={props.showRegLabel}
        showRegToggle={props.showRegToggle}
        defaultDurationType={props.defaultDurationType}
        daysOnly={props.daysOnly}
        useBanner={props.useBanner}
        bannerCoverType={props.bannerCoverType}
        bannerCoverDuration={props.bannerCoverDuration}
        checkboxLabel={props.checkboxLabel}
        formIntroText={props.formIntroText}
        submitButtonText={props.submitButtonText}
        submitButtonColor={props.submitButtonColor}
        regPlateOnly={props.regPlateOnly}
        showDurationPicker={props.showDurationPicker}
      />
    </Card>

    <h1 className="order-1 mt-0 self-end pt-0 md:mb-5">{props.title}</h1>
    <div className="order-2">
      {props.subtitle && <h2 className="tc-heading-sm mb-0 p-0 md:mt-0">{props.subtitle}</h2>}
    </div>

    <div className="order-3 self-start">
      {props.tagline && <div dangerouslySetInnerHTML={{ __html: props.tagline }} className="tc-heading-sm" />}
      <BodyContent props={props} />
    </div>
  </div>
);

const hasBodyContent = (props: ProductPageHeaderProps): boolean => {
  if (props.contentType === CONTENT_TYPES.LIST && props.points?.length) return true;
  if (props.contentType === CONTENT_TYPES.TRUSTPILOT) return true;
  if (props.contentType === CONTENT_TYPES.TEXT && props.body) return true;
  if (props.showAppLinks && props.appLinks?.value?.data.appStoreLink && props.appLinks?.value?.data.playStoreLink)
    return true;
  return false;
};

const StandardLayout: React.FC<{ props: ProductPageHeaderProps }> = ({ props }) => {
  const hasImage = !!props.image;
  const imageOnLeft = props.imagePosition === 'left';

  return (
    <div className={['grid gap-6', hasImage && 'md:grid-cols-2 md:gap-8'].filter(Boolean).join(' ')}>
      <div
        className={[
          'order-2 flex flex-col gap-4',
          hasImage && (imageOnLeft ? 'md:order-2' : 'md:order-1'),
          hasImage && 'self-center',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {props.badge && (
          <div>
            <span className="bg-primary text-neutral inline-block rounded-[6px] border-2 border-white px-4 py-2 text-[14px] leading-none font-bold">
              {props.badge}
            </span>
          </div>
        )}
        <h1 className="my-0">{props.title}</h1>
        {props.subtitle && <h2 className="tc-subheading my-0">{props.subtitle}</h2>}
        {props.tagline && (
          <div dangerouslySetInnerHTML={{ __html: props.tagline }} className="tc-subheading [&>p]:my-0" />
        )}
        {hasBodyContent(props) && <BodyContent props={props} />}
      </div>

      {hasImage && (
        <div className={['shadow-tc order-1 overflow-hidden', imageOnLeft ? 'md:order-1' : 'md:order-2'].join(' ')}>
          <Image
            src={props.image!}
            alt={props.title}
            width={1024}
            height={683}
            className={`h-[142px] w-full md:h-auto md:min-h-[379px] ${props.imageFit === 'contain' ? 'object-contain' : 'object-cover'}`}
            sizes="(max-width: 768px) 100vw, 50vw"
            priority
            fetchPriority="high"
          />
        </div>
      )}
    </div>
  );
};

const BodyContent: React.FC<{ props: ProductPageHeaderProps }> = ({ props }) => {
  return (
    <div className="mt-5 md:mb-10">
      {props.contentType === CONTENT_TYPES.LIST && (
        <ul className="m-0 p-0">
          {props.points?.map(({ point }, index) => (
            <li key={index} className="mb-4 flex items-center md:max-w-[80%]">
              <div className="tc-icon mr-4 rounded-md p-2">
                <IconCheck size={32} color="#FFF" />
              </div>
              {point}
            </li>
          ))}
        </ul>
      )}

      {props.contentType === CONTENT_TYPES.TRUSTPILOT && (
        <div className="hidden md:block">
          <TrustPilotWidget layout={TrustpilotLayouts.Mini} />
        </div>
      )}

      {(props.contentType === CONTENT_TYPES.TEXT || props.contentType === CONTENT_TYPES.TRUSTPILOT) && (
        <div dangerouslySetInnerHTML={{ __html: props.body || '' }} />
      )}

      {props.showAppLinks && props.appLinks?.value?.data.appStoreLink && props.appLinks?.value?.data.playStoreLink && (
        <div className="mt-4 flex gap-4">
          <AppStoreLinks
            appStore={props.appLinks.value.data.appStoreLink}
            playStore={props.appLinks.value.data.playStoreLink}
          />
        </div>
      )}
    </div>
  );
};

export const PageHero: React.FC<ProductPageHeaderProps> = (props) => {
  const { enableForm, headingOnly, title } = props;

  const renderForm = useCallback(() => {
    if (enableForm) {
      return <FormLayout props={props} />;
    }

    return <StandardLayout props={props} />;
  }, [enableForm, props]);

  return <div className="tc-container py-8">{headingOnly ? <h1>{title}</h1> : renderForm()}</div>;
};
